import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'configurations';

// Action Types
export const types = {
  GET_CONFIGURATIONS: createAsyncTypes(`${KEY}/GET_CONFIGURATIONS`),
  CREATE_CONFIGURATION: createAsyncTypes(`${KEY}/CREATE_CONFIGURATION`),
  UPDATE_CONFIGURATION: createAsyncTypes(`${KEY}/UPDATE_CONFIGURATION`),
  DELETE_CONFIGURATION: createAsyncTypes(`${KEY}/DELETE_CONFIGURATION`),
  RESET_ACTION: `${KEY}/RESET_ACTION`,
  RESET_ERROR: `${KEY}/RESET_ERROR`
};

type SupportedLocales =
  | 'enUS'
  | 'frCA'
  | 'esMX'
  | 'deDE'
  | 'frFR'
  | 'enGB'
  | 'itIT'
  | 'esES'
  | 'ptBR'
  | 'ptPT'
  | 'zhCN'
  | 'jaJP'
  | 'koKR'
  | 'viVN';

export type LocalizedStrRecord = Partial<Record<SupportedLocales, string>>;
export type LocalizedStrListRecord = Partial<Record<SupportedLocales, string[]>>;

export interface Messages {
  readonly header?: LocalizedStrRecord;
  readonly subheader?: LocalizedStrRecord;
  readonly buttons?: LocalizedStrListRecord;
  readonly placeholder?: LocalizedStrRecord;
  /** OLD naming convention, replacing with 'subheader' */
  readonly message?: LocalizedStrRecord;
}

/** OLD format of the localized JSON, replacing with 'Messages' */
export interface Message {
  header?: string;
  message?: string;
  buttons?: string[];
  footer?: string;
  placeholder?: string;
}

export interface CartRule {
  readonly dependentIds: string[];
  readonly reliesOnIds: string[];
}

export interface CartDependencies {
  readonly event?: string[];
  readonly genre?: string[];
  readonly package?: string[];
  readonly donation?: boolean;
}

export interface LanguageField {
  displayText: string;
  langCode: SupportedLocales;
  isDefault?: boolean;
}

export interface ConfigInputField {
  apiParam: string;
  inputType: string;
  label: LocalizedStrRecord;
  required?: boolean;
  keyboard?: string;
  halfWidth?: boolean;
  minChar?: number;
  maxChar?: number;
  inputMask?: string;
  placeholder?: LocalizedStrRecord;
}

export interface SurveyField extends ConfigInputField {
  surveyApiParam?: string;
}

interface NavigationOption {
  enabled: boolean;
  messages?: Messages;
}

interface SearchOption {
  readonly enabled?: boolean;
  readonly name?: string;
  readonly label?: LocalizedStrRecord;
  readonly lookupOption?: {
    readonly fields?: ConfigInputField[];
    readonly messages?: Messages;
  };
}
interface ScanMembershipCard {
  readonly enabled?: boolean;
  readonly label?: LocalizedStrRecord;
  readonly messages?: Messages;
}

interface MembershipLookup {
  readonly enabled?: boolean;
  readonly messages?: Messages;
  readonly scanMembershipCard?: ScanMembershipCard;
  readonly customSearchOptions?: SearchOption;
  readonly tileOrder?: string[];
  readonly notFoundMessages?: Messages;
  readonly loadingMessages?: Messages;
}

interface MembershipSalesSettings {
  readonly name: string;
  readonly benefits?: {
    readonly [key: string]: LocalizedStrRecord;
  };
  readonly levelSelection?: {
    readonly messages?: Messages;
  };
  readonly dataCollection?: {
    readonly fields?: ConfigInputField[];
    readonly messages?: Messages;
    readonly confirmationOverlay?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
  };
  readonly reservation?: {
    readonly loadingMessages?: Messages;
    readonly errorMessages?: Messages;
    readonly renewalOverlayMessages?: Messages;
    readonly upgradeOverlayMessages?: Messages;
  };
}

export interface LocationValidation {
  readonly enabled?: boolean;
  readonly overlayMessages?: Messages;
  readonly dataCollection?: {
    readonly keyboard?: string;
    readonly messages?: Messages;
  };
  readonly errorOverlayMessages?: Messages;
  readonly resultCriteria?: {
    readonly cities?: string[];
    readonly counties?: string[];
    readonly states?: string[];
    readonly countries?: string[];
  };
}

export interface BaseTicketing {
  readonly priceTypeLimit?: number;
  readonly orderLimit?: number;
  readonly supportName?: LocalizedStrRecord;
  readonly filterByName?: boolean;
  readonly filterByDate?: boolean;
  readonly genreSelection?: boolean;
  readonly todayEventFlag?: boolean;
  readonly autoSelectEvent?: boolean;
  readonly allowPaymentCancelation?: boolean;
  readonly cartRules?: CartRule[];
  readonly customerIdentification?: {
    readonly lookup?: {
      readonly enabled?: boolean;
      readonly lookupList?: {
        readonly fields?: ConfigInputField[];
        readonly subFields?: SurveyField[];
        readonly messages?: Messages;
        readonly cartDependencies?: CartDependencies;
      }[];
      readonly notFound?: {
        readonly enabled?: boolean;
        readonly messages?: Messages;
      };
    };
    readonly creation?: {
      readonly enabled?: boolean;
      readonly creationList?: {
        readonly fields?: ConfigInputField[];
        readonly subFields?: SurveyField[];
        readonly messages?: Messages;
        readonly cartDependencies?: CartDependencies;
      }[];
    };
  };
  readonly ticketDeliveryOptions?: {
    readonly email?: boolean;
    readonly textMessage?: boolean;
    readonly print?: boolean;
  };
  readonly eventTierAvailability?: {
    readonly lowAvailabilityThreshold?: number;
    readonly soldOutThreshold?: number;
    readonly showSoldOut?: boolean;
    readonly messages?: {
      readonly normal?: LocalizedStrRecord;
      readonly lowAvailability?: LocalizedStrRecord;
      readonly soldOut?: LocalizedStrRecord;
      readonly unlimited?: LocalizedStrRecord;
    };
  };
  readonly productAvailabilityAndSelection?: {
    readonly autoLaunchProduct?: string[];
    readonly upsell?: {
      readonly productSelectionMessages?: Messages;
      readonly tierSelectionMessages?: Messages;
      readonly ticketSelectionMessages?: Messages;
    };
  };
  readonly survey?: {
    readonly enabled?: boolean;
    readonly surveyList?: {
      readonly surveyFields?: SurveyField[];
      readonly subSurveyFields?: SurveyField[];
      readonly cartDependencies?: CartDependencies;
      readonly messages?: Messages;
    }[];
  };
  readonly eventDisplaySettingsList?: {
    readonly name?: string;
    readonly showDate?: boolean;
    readonly showTime?: boolean;
    readonly showLocation?: boolean;
    readonly showTier?: boolean;
    readonly showCartItemsPrice?: boolean;
    readonly autoSelectTier?: boolean;
  }[];
  readonly donations?: {
    readonly amounts?: number[];
    readonly showInProductSelection?: boolean;
    readonly productName?: LocalizedStrRecord;
    readonly messages?: Messages;
    readonly giftAid?: {
      readonly enabled?: boolean;
      readonly messages?: {
        readonly optIn?: LocalizedStrRecord;
        readonly details?: LocalizedStrRecord;
      };
    };
    readonly overlay?: {
      readonly enabled?: boolean;
      readonly roundup?: {
        readonly enabled?: boolean;
        readonly amount?: number;
        readonly message?: LocalizedStrRecord;
      };
      readonly messages?: Messages;
    };
    readonly cart?: {
      readonly messages?: Messages;
      readonly exitOverlayMessages?: Messages;
      readonly addMoreTickets?: boolean;
      readonly removeTickets?: {
        readonly enabled?: boolean;
        readonly message?: LocalizedStrRecord;
      };
      readonly seatDetailsOverlayMessage?: LocalizedStrRecord;
      readonly expandItems?: boolean;
      readonly expirationOverlayMessages?: Messages;
      readonly giftVoucher?: {
        readonly enabled?: boolean;
        readonly messages?: Messages;
      };
      readonly summary?: {
        readonly enabled?: boolean;
        readonly messages?: Messages;
      };
    };
  };
  readonly priorityPhoneCountryCodes?: string[];
  readonly priorityPostalCodeLookupCountries?: string[];
}

export interface Ticketing extends BaseTicketing {
  readonly membershipLookup?: MembershipLookup;
  readonly membershipSalesSettingsList?: MembershipSalesSettings[];
  readonly showPriceRange?: boolean;
  readonly promoCode?: {
    readonly priceType?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
    readonly cart?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
    readonly selection?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
      readonly manualEntry?: {
        readonly enabled?: boolean;
        readonly messages?: LocalizedStrRecord;
      };
      readonly customOptions?: {
        readonly code?: string;
        readonly messages?: LocalizedStrRecord;
        readonly infoOverlay?: {
          readonly enabled?: boolean;
          readonly messages?: Messages;
        };
      }[];
      readonly tileOrder?: string[];
    };
  };
  readonly locationValidation?: LocationValidation;
}

export interface WillCall {
  readonly skipTicketList?: boolean;
  readonly expandedOrderItems?: boolean;
  readonly selectAllTickets?: boolean;
  readonly scanMembershipCard?: ScanMembershipCard;
  readonly willCallTileOrder?: string[];
  readonly customSearch?: {
    readonly enabled?: boolean;
    readonly tileOptions?: SearchOption[];
  };
  readonly errorMessages?: Messages;
  readonly messages?: Messages;
}

export interface Messaging {
  readonly reservingTicket?: Message;
  readonly ticketsUnavailable?: Message;
  readonly eventAPIDown?: Message;
  readonly printerError?: Message;
  readonly purchaseError?: Message;
  readonly globalError?: Message;
  readonly ticketServiceDown?: Message;
  readonly checkoutCompletion?: Message;
  readonly ticketSelectionPage?: Message;
  readonly genrePage?: Message;
  readonly tierPage?: Message;
  readonly purchasePaymentPage?: Message;
  readonly emailSuggestions?: Message;
  readonly eventsPage?: Message;
  readonly orderListPage?: Message;
  readonly packageTierPage?: Message;
  readonly taxesAndFeesLabel?: Message;
  readonly deliveryEmailCollection?: Message;
}

export interface InfoContent {
  header: string;
  subHeader?: string;
  description?: string;
  image?: string;
}

export interface InfoView {
  label: string;
  content: InfoContent[];
}

export interface ConfigurationColumn {
  readonly maintenanceLoginTimeout?: number;
  readonly maintenanceTimeout?: number;
  readonly maintenanceTimerCount?: number;
  readonly hideStepper?: boolean;
  readonly languageSettings?: LanguageField[];
  readonly flowTemplate?: undefined | string;
  readonly navigationSelection?: {
    ticketing?: NavigationOption;
    willCall?: NavigationOption;
    info?: NavigationOption;
    cityPass?: NavigationOption;
    donations?: NavigationOption;
    tileOrder?: string[];
    messages?: Messages;
  };
  readonly globalSettings?: {
    readonly fonts?: {
      readonly primary: string;
      readonly secondary: string;
    };
    readonly palette?: {
      readonly darkPrimary: string;
      readonly lightPrimary: string;
      readonly primary: string;
      readonly secondary: string;
      readonly textIcons: string;
      readonly accent: string;
      readonly primaryText: string;
      readonly secondaryText: string;
      readonly lightSecondary: string;
      readonly darkSecondary: string;
      readonly divider: string;
    };
    readonly upperDeck?: {
      readonly type: string;
      readonly order: string[];
      readonly transitionTime?: number;
    };
    readonly attractLoop?: {
      readonly name?: LocalizedStrRecord;
      readonly type: string;
      readonly order: string[];
      readonly transitionTime?: number;
      linkedContentUrl?: string;
      readonly messages?: {
        readonly header?: LocalizedStrRecord;
        readonly message?: LocalizedStrRecord;
      };
      readonly languageTransitionTime?: number;
      readonly fontColor?: string;
    };
    readonly splashPage?: {
      readonly enabled?: boolean;
      readonly messages?: {
        readonly header?: LocalizedStrRecord;
        readonly message?: LocalizedStrRecord;
        readonly buttons?: LocalizedStrListRecord;
      };
    };
    readonly inactivityTimeout?: {
      readonly duration?: number;
      readonly navigationPrompt?: {
        readonly enabled?: boolean;
        readonly duration?: number;
        readonly buttonZoomSize?: number;
        readonly buttonZoomSpeed?: number;
      };
      readonly abandonSession?: {
        readonly enabled?: boolean;
        readonly duration?: number;
        readonly messages?: Messages;
      };
    };
    readonly touchForward?: {
      purchaseFlow?: {
        enabled?: boolean;
      };
      general?: {
        enabled?: boolean;
      };
      ticketDelivery?: {
        enabled?: boolean;
      };
    };
  };
  readonly labels?: {
    readonly cartItem?: LocalizedStrRecord;
    readonly discount?: LocalizedStrRecord;
    readonly product?: {
      singular?: LocalizedStrRecord;
      plural?: LocalizedStrRecord;
    };
    readonly cart?: LocalizedStrRecord;
  };
  readonly inactivePage?: {
    readonly languageTransitionTime?: number;
    readonly messages?: Messages;
  };
}

export interface CityPass {
  readonly ticketing?: BaseTicketing;
  readonly ticketEntry?: {
    readonly passLimit?: number;
    readonly scanning?: {
      readonly messages?: Messages;
    };
    readonly manualEntry?: {
      readonly enabled: boolean;
      readonly messages?: Messages;
    };
  };
}

export interface Configuration {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly clientId: string;
  readonly ticketing: Ticketing;
  readonly willCall: WillCall;
  readonly messaging: Partial<Record<SupportedLocales, Messaging>>;
  readonly infoView: undefined | Partial<Record<SupportedLocales, InfoView>>;
  readonly configuration: ConfigurationColumn;
  readonly cityPass: undefined | CityPass;
}

export interface ConfigurationState {
  readonly configurations: Configuration[];
  readonly action: {
    readonly pending: boolean;
    readonly success: boolean;
    readonly error: string | null;
  };
}

export interface CreateConfigurationPayload {
  readonly name: string;
  readonly ticketing?: Ticketing;
  readonly willCall?: WillCall;
  readonly messaging?: Partial<Record<SupportedLocales, Messaging>>;
  readonly infoView?: undefined | Partial<Record<SupportedLocales, InfoView>>;
  readonly configuration?: ConfigurationColumn;
}

export interface GetConfigurationsRequest {
  type: typeof types.GET_CONFIGURATIONS.REQUEST;
}

export interface GetConfigurationsPending {
  type: typeof types.GET_CONFIGURATIONS.PENDING;
  payload: boolean;
}

export interface GetConfigurationsSuccess {
  type: typeof types.GET_CONFIGURATIONS.SUCCESS;
  payload: Configuration[];
}

export interface GetConfigurationsError {
  type: typeof types.GET_CONFIGURATIONS.ERROR;
  payload: string;
}

export interface CreateConfigurationRequest {
  type: typeof types.CREATE_CONFIGURATION.REQUEST;
  payload: CreateConfigurationPayload;
}

export interface CreateConfigurationPending {
  type: typeof types.CREATE_CONFIGURATION.PENDING;
  payload: boolean;
}

export interface CreateConfigurationSuccess {
  type: typeof types.CREATE_CONFIGURATION.SUCCESS;
}

export interface CreateConfigurationError {
  type: typeof types.CREATE_CONFIGURATION.ERROR;
  payload: string;
}

export type UpdateConfigurationPayload = Partial<Configuration> & { id: string };

export interface UpdateConfigurationRequest {
  type: typeof types.UPDATE_CONFIGURATION.REQUEST;
  payload: UpdateConfigurationPayload;
}

export interface UpdateConfigurationPending {
  type: typeof types.UPDATE_CONFIGURATION.PENDING;
  payload: boolean;
}

export interface UpdateConfigurationSuccess {
  type: typeof types.UPDATE_CONFIGURATION.SUCCESS;
  payload: Partial<Configuration>;
}

export interface UpdateConfigurationError {
  type: typeof types.UPDATE_CONFIGURATION.ERROR;
  payload: string;
}

export interface DeleteConfigurationRequest {
  type: typeof types.DELETE_CONFIGURATION.REQUEST;
  payload: { id: string };
}

export interface DeleteConfigurationPending {
  type: typeof types.DELETE_CONFIGURATION.PENDING;
  payload: boolean;
}

export interface DeleteConfigurationSuccess {
  type: typeof types.DELETE_CONFIGURATION.SUCCESS;
}

export interface DeleteConfigurationError {
  type: typeof types.DELETE_CONFIGURATION.ERROR;
  payload: string;
}

export interface ResetAction {
  type: typeof types.RESET_ACTION;
}

export interface ResetError {
  type: typeof types.RESET_ERROR;
}

export type ConfigurationsActionTypes =
  | GetConfigurationsRequest
  | GetConfigurationsPending
  | GetConfigurationsSuccess
  | GetConfigurationsError
  | CreateConfigurationRequest
  | CreateConfigurationPending
  | CreateConfigurationSuccess
  | CreateConfigurationError
  | UpdateConfigurationRequest
  | UpdateConfigurationPending
  | UpdateConfigurationSuccess
  | UpdateConfigurationError
  | DeleteConfigurationRequest
  | DeleteConfigurationPending
  | DeleteConfigurationSuccess
  | DeleteConfigurationError
  | ResetAction
  | ResetError;
